import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useAuthPrompt } from '../App';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Send,
  DollarSign,
  Clock,
  Check,
  AlertCircle,
  Trash2,
  Edit,
  Copy,
  Download,
  X,
  Loader2,
  User,
  Mail,
  Calendar,
  ChevronRight,
  Users
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
};

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-500/10 text-slate-500 border-slate-500/30', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500/10 text-blue-500 border-blue-500/30', icon: Send },
  paid: { label: 'Paid', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30', icon: Check },
  overdue: { label: 'Overdue', color: 'bg-red-500/10 text-red-500 border-red-500/30', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500/10 text-gray-500 border-gray-500/30', icon: X }
};

const emptyInvoice = {
  client_name: '',
  client_email: '',
  client_address: '',
  issue_date: new Date().toISOString().split('T')[0],
  due_date: '',
  items: [{ description: '', quantity: 1, rate: 0 }],
  tax_rate: 0,
  notes: '',
  terms: 'Payment due within 30 days.'
};

export default function Invoices() {
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [formData, setFormData] = useState(emptyInvoice);
  const [saving, setSaving] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'all') params.status = activeTab;
      
      const response = await axios.get(`${API_URL}/api/business/invoices`, {
        params,
        withCredentials: true
      });
      setInvoices(response.data.invoices || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/business/clients`, {
        withCredentials: true
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchClients();
  }, [fetchInvoices, fetchClients]);

  // Handle client param from URL (when coming from Clients page)
  useEffect(() => {
    const clientId = searchParams.get('client');
    if (clientId && clients.length > 0) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setSelectedClientId(clientId);
        handleClientSelect(clientId);
        setShowForm(true);
        // Clear the URL param
        setSearchParams({});
      }
    }
  }, [searchParams, clients, setSearchParams]);

  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);
    if (clientId) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        // Build full address
        let address = client.address || '';
        if (client.city || client.state || client.zip_code) {
          address += address ? '\n' : '';
          address += `${client.city || ''}, ${client.state || ''} ${client.zip_code || ''}`.trim();
        }
        
        setFormData(prev => ({
          ...prev,
          client_id: clientId,
          client_name: client.company || client.name,
          client_email: client.email || '',
          client_address: address,
          tax_rate: client.tax_rate || prev.tax_rate
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        client_id: '',
        client_name: '',
        client_email: '',
        client_address: ''
      }));
    }
  };

  const handleCreateNew = () => {
    setEditingInvoice(null);
    setFormData(emptyInvoice);
    setSelectedClientId('');
    setShowForm(true);
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    setSelectedClientId(invoice.client_id || '');
    setFormData({
      client_id: invoice.client_id || '',
      client_name: invoice.client_name || '',
      client_email: invoice.client_email || '',
      client_address: invoice.client_address || '',
      issue_date: invoice.issue_date || '',
      due_date: invoice.due_date || '',
      items: invoice.items || [{ description: '', quantity: 1, rate: 0 }],
      tax_rate: invoice.tax_rate || 0,
      notes: invoice.notes || '',
      terms: invoice.terms || ''
    });
    setShowForm(true);
    setSelectedInvoice(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!formData.client_name) {
      toast.error('Client name is required');
      return;
    }

    if (formData.items.length === 0 || !formData.items.some(item => item.description)) {
      toast.error('At least one line item is required');
      return;
    }

    try {
      setSaving(true);
      
      if (editingInvoice) {
        await axios.put(`${API_URL}/api/business/invoices/${editingInvoice.id}`, formData, {
          withCredentials: true
        });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API_URL}/api/business/invoices`, formData, {
          withCredentials: true
        });
        toast.success('Invoice created');
      }
      
      setShowForm(false);
      setFormData(emptyInvoice);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    try {
      await axios.patch(`${API_URL}/api/business/invoices/${invoiceId}/status`, 
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Invoice marked as ${newStatus}`);
      if (newStatus === 'paid') {
        toast.success('Income transaction created automatically');
      }
      fetchInvoices();
      setSelectedInvoice(null);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDuplicate = async (invoiceId) => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    try {
      await axios.post(`${API_URL}/api/business/invoices/${invoiceId}/duplicate`, {}, {
        withCredentials: true
      });
      toast.success('Invoice duplicated');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to duplicate invoice');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!isAuthenticated) {
      showAuthPrompt();
      return;
    }

    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      await axios.delete(`${API_URL}/api/business/invoices/${invoiceId}`, {
        withCredentials: true
      });
      toast.success('Invoice deleted');
      fetchInvoices();
      setSelectedInvoice(null);
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, rate: 0 }]
    });
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (formData.tax_rate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/api/business/invoices/${invoiceId}/pdf`, {
        responseType: 'blob',
        withCredentials: true
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage invoices for your clients
          </p>
        </div>
        <Button onClick={handleCreateNew} data-testid="create-invoice-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-emerald-500">
                  {formatCurrency(stats.total_revenue || 0)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-amber-500">
                  {formatCurrency(stats.pending_amount || 0)}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500/40" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sent</p>
                <p className="text-2xl font-bold">{stats.sent_count || 0}</p>
              </div>
              <Send className="w-8 h-8 text-muted-foreground/40" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Paid</p>
                <p className="text-2xl font-bold text-emerald-500">{stats.paid_count || 0}</p>
              </div>
              <Check className="w-8 h-8 text-emerald-500/40" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Form Modal */}
      {showForm && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {editingInvoice ? 'Edit Invoice' : 'New Invoice'}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              {/* Client Selector */}
              {clients.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="client_select">Select Client (Optional)</Label>
                  <div className="flex gap-2">
                    <select
                      id="client_select"
                      value={selectedClientId}
                      onChange={(e) => handleClientSelect(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border bg-background"
                      data-testid="client-select"
                    >
                      <option value="">-- Enter client details manually --</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.company || client.name} {client.company && client.name ? `(${client.name})` : ''}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => window.open('/business/clients', '_blank')}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Manage
                    </Button>
                  </div>
                </div>
              )}

              {/* Client Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client_name">Client Name *</Label>
                  <Input
                    id="client_name"
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    placeholder="Client or Company Name"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_email">Client Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="client@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client_address">Address</Label>
                  <Input
                    id="client_address"
                    value={formData.client_address}
                    onChange={(e) => setFormData({...formData, client_address: e.target.value})}
                    placeholder="Client Address"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issue_date">Issue Date</Label>
                  <Input
                    id="issue_date"
                    type="date"
                    value={formData.issue_date}
                    onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <Label>Line Items</Label>
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-20"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Rate"
                      value={item.rate}
                      onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                      className="w-28"
                    />
                    <div className="w-24 py-2 text-right font-medium">
                      {formatCurrency(item.quantity * item.rate)}
                    </div>
                    {formData.items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Line Item
                </Button>
              </div>

              {/* Tax & Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">Tax (%)</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.tax_rate}
                      onChange={(e) => setFormData({...formData, tax_rate: parseFloat(e.target.value) || 0})}
                      className="w-20 h-8"
                    />
                    <span className="font-medium w-24 text-right">{formatCurrency(calculateTax())}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-primary">{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>

              {/* Notes & Terms */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="terms">Payment Terms</Label>
                  <Textarea
                    id="terms"
                    value={formData.terms}
                    onChange={(e) => setFormData({...formData, terms: e.target.value})}
                    placeholder="Payment terms..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tabs & List */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All ({stats.total_invoices || 0})</TabsTrigger>
          <TabsTrigger value="draft">Draft ({stats.draft_count || 0})</TabsTrigger>
          <TabsTrigger value="sent">Sent ({stats.sent_count || 0})</TabsTrigger>
          <TabsTrigger value="paid">Paid ({stats.paid_count || 0})</TabsTrigger>
          <TabsTrigger value="overdue">Overdue ({stats.overdue_count || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-lg font-medium mb-2">No invoices yet</p>
                <p className="text-muted-foreground mb-4">Create your first invoice to get started</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Invoice
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {invoices.map((invoice) => {
                const status = statusConfig[invoice.status] || statusConfig.draft;
                const StatusIcon = status.icon;
                
                return (
                  <Card
                    key={invoice.id}
                    className={`cursor-pointer hover:border-primary/30 transition-colors ${
                      selectedInvoice?.id === invoice.id ? 'border-primary' : ''
                    }`}
                    onClick={() => setSelectedInvoice(selectedInvoice?.id === invoice.id ? null : invoice)}
                    data-testid={`invoice-${invoice.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${status.color}`}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{invoice.invoice_number}</span>
                              <Badge variant="outline" className={status.color}>
                                {status.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{invoice.client_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{formatCurrency(invoice.total)}</p>
                          <p className="text-xs text-muted-foreground">
                            {invoice.due_date ? `Due ${formatDate(invoice.due_date)}` : `Issued ${formatDate(invoice.issue_date)}`}
                          </p>
                        </div>
                      </div>

                      {/* Expanded View */}
                      {selectedInvoice?.id === invoice.id && (
                        <div className="mt-4 pt-4 border-t space-y-4">
                          {/* Line Items */}
                          <div className="space-y-2">
                            {invoice.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">{item.description}</span>
                                <span>{item.quantity} x {formatCurrency(item.rate)} = {formatCurrency(item.quantity * item.rate)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between font-medium pt-2 border-t">
                              <span>Subtotal</span>
                              <span>{formatCurrency(invoice.subtotal)}</span>
                            </div>
                            {invoice.tax_amount > 0 && (
                              <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Tax ({invoice.tax_rate}%)</span>
                                <span>{formatCurrency(invoice.tax_amount)}</span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={(e) => { e.stopPropagation(); handleDownloadPdf(invoice.id, invoice.invoice_number); }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleEdit(invoice); }}>
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleDuplicate(invoice.id); }}>
                              <Copy className="w-4 h-4 mr-1" />
                              Duplicate
                            </Button>
                            {invoice.status === 'draft' && (
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleStatusChange(invoice.id, 'sent'); }}>
                                <Send className="w-4 h-4 mr-1" />
                                Mark Sent
                              </Button>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={(e) => { e.stopPropagation(); handleStatusChange(invoice.id, 'paid'); }}>
                                <Check className="w-4 h-4 mr-1" />
                                Mark Paid
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-500 hover:text-red-600"
                              onClick={(e) => { e.stopPropagation(); handleDelete(invoice.id); }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
